import { Injectable } from '@angular/core';

import { Banner } from '../../types/banner';
import { BodyService } from '../body/body.service';
import { Document } from '../../models/document';
import { Folder } from '../../models/folder';
import { Profile } from '../../models/profile';
import { SystemUser } from '../../models/system-user';

@Injectable({
  providedIn: 'root'
})
export class BannerService {
  
  private _banner: Banner = 'None';
  private _isClosing: boolean = false;
  private _isOpen: boolean = false;
  private _message: string = '';

  private _currentFolderId?: string;
  private _document?: Document;
  private _file?: File;
  private _folder?: Folder;
  private _profile?: Profile;
  private _systemUser?: SystemUser;

  constructor(
    private readonly _bodyService: BodyService
  ) { }

  public get banner(): Banner {
    return this._banner;
  }

  public get isOpen(): boolean {
    return this._isOpen;
  }

  public get isClosing(): boolean {
    return this._isClosing;
  }

  public get message(): string {
    return this._message;
  }

  public get file(): File | undefined {
    return this._file;
  }
  
  public get folder(): Folder | undefined {
    return this._folder;
  }

  public get profile(): Profile | undefined {
    return this._profile;
  }

  public get document(): Document | undefined {
    return this._document;
  }

  public get systemUser(): SystemUser | undefined {
    return this._systemUser;
  }

  public get currentFolderId(): string | undefined {
    return this._currentFolderId;
  }

  /**
   * Reset banner service data. 
   */
  private clearBanner = (): void => {
    this._banner = 'None';
    this._document = undefined;
    this._folder = undefined;
    this._isClosing = false;
    this._isOpen = false;
    this._message = '';
    this._profile = undefined;
    this._systemUser = undefined;
    this._bodyService.unlockBody();
  }

  /**
   * Open specific banner.
   * @param banner Banner type to open.
   */
  public openBanner = (banner: Banner): void => {
    this._banner = banner;
    this._isOpen = true;
    this._bodyService.blockBody();
  }

  /**
   * Close banners with data reset.
   */
  public closeBanner = (): void => {
    this._isClosing = true;
    setTimeout(() => this.clearBanner(), 1000);
  }

  /**
   * Set current Folder unique identifier.
   * @param currentFolderId Current Folder unique identifier.
   */
  public setCurrentFolderId = (currentFolderId?: string): void => {
    setTimeout(() => this._currentFolderId = currentFolderId, 250);
  }

  /**
   * Open banner to create SystemUser.
   */
  public openCreateSystemUser = (): void => this.openBanner('CreateSystemUser');

  /**
   * Open banner to edit SystemUser.
   * @param systemUser SystemUser selected. 
   */
  public openEditSystemUser = (systemUser: SystemUser): void => {
    this._systemUser = systemUser;
    this.openBanner('EditSystemUser');
  };

  /**
   * Open banner to batch CreateSystemUser.
   * @param systemUser SystemUser selected.
   */
  public openBatchCreateSystemUser = (systemUser: SystemUser): void => {
    this._systemUser = systemUser;
    this.openBanner('BatchCreateSystemUser');
  };

  /**
   * Open banner to batch inactivate SystemUser.
   * @param systemUser SystemUser selected.
   */
  public openBatchInactivateSystemUser = (systemUser: SystemUser): void => {
    this._systemUser = systemUser;
    this.openBanner('BatchInactivateSystemUser');
  };

  /**
   * Open banner to assign Profiles to SystemUser.
   * @param systemUser SystemUser selected.
   */
  public openAssignProfilesToSystemUser = (systemUser: SystemUser): void => {
    this._systemUser = systemUser;
    this.openBanner('AssignProfilesToSystemUser');
  };

  /**
   * Open banner to create Profile.
   */
  public openCreateProfile = (): void => this.openBanner('CreateProfile');

  /**
   * Open banner to edit Profile.
   * @param profile Profile selected.
   */
  public openEditProfile = (profile: Profile): void => {
    this._profile = profile;
    this.openBanner('EditProfile');
  };

  /**
   * Open banner to batch create Profile.
   * @param profile Profile selected.
   */
  public openBatchCreateProfile = (profile: Profile): void => {
    this._profile = profile;
    this.openBanner('BatchCreateProfile');
  };

  /**
   * Open banner to batch inactivate Profile.
   * @param profile Profile selected.
   */
  public openBatchInactivateProfile = (profile: Profile): void => {
    this._profile = profile;
    this.openBanner('BatchInactivateProfile');
  };

  /**
   * Open banner to assign SystemUsers to Profile.
   * @param profile Profile selected.
   */
  public openAssignSystemUsersToProfile = (profile: Profile): void => {
    this._profile = profile;
    this.openBanner('AssignSystemUsersToProfile');
  };

  /**
   * Open banner to create Folder.
   */
  public openCreateFolder = (): void => this.openBanner('CreateFolder');

  /**
   * Open banner to edit Folder.
   */
  public openEditFolder = (): void => this.openBanner('EditFolder');

  /**
   * Open banner to delete Folder.
   */
  public openDeleteFolder = (message: string): void => {
    this._message = message;
    this.openBanner('DeleteFolder');
  }

  /**
   * Open banner to assign Profiles to Folder.
   */
  public openAssignProfilesToFolder = (): void => this.openBanner('AssignProfilesToFolder');

  /**
   * Open banner to assign SystemUsers to Folder.
   */
  public openAssignSystemUsersToFolder = (): void => this.openBanner('AssignSystemUsersToFolder');

  /**
   * Open banner to create Document.
   */
  public openCreateDocument = (): void => this.openBanner('CreateDocument');

  /**
   * Open banner to edit Document.
   */
  public openEditDocument = (): void => this.openBanner('EditDocument');

  /**
   * Open banner to delete Document.
   */
  public openDeleteDocument = (message: string): void => {
    this.openBanner('DeleteDocument');
    this._message = message;
  }

  /**
   * Open Annexes banner.
   */
  public openAnnexes = (): void => this.openBanner('Annexes');

  /**
   * Open DatePicker banner.
   */
  public openDatePicker = (): void => this.openBanner('DatePicker'); 
}